import * as React from 'react';
import {
    Checkbox,
    CommandBar,
    DefaultButton,
    DetailsList,
    DetailsListLayoutMode,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    IDropdownOption,
    IColumn,
    ICommandBarItemProps,
    IconButton,
    Label,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Selection,
    SelectionMode,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
} from '@fluentui/react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiHelper } from '../../../ApiHelper';
import {
    AgentTaskSummaryResult,
    PageShell,
    StatCard,
    SyntheticBanner,
    formatNumber,
    useDocumentTitle,
} from '../shared';

export function AgentTaskSummaryPage() {
    useDocumentTitle('Agent Task Summary');

    const navigate = useNavigate();
    const { serviceName } = useParams();
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [result, setResult] = React.useState<AgentTaskSummaryResult | null>(null);

    React.useEffect(() => {
        if (!serviceName) {
            return;
        }

        setLoading(true);
        setError(null);

        ApiHelper(`/api/service/${serviceName}/agenttasks/summary`)
            .then((response: AgentTaskSummaryResult) => {
                setResult(response);
                setLoading(false);
            })
            .catch((err: Error) => {
                setError(err.message || 'Failed to load the summary.');
                setResult(null);
                setLoading(false);
            });
    }, [serviceName]);

    return (
        <PageShell title="Agent Task Summary">
            <SyntheticBanner visible={!!result?.isSynthetic} />

            <DefaultButton text="Back to lists" onClick={() => navigate(`/service/${serviceName}/agenttasks`)} />

            {error && (
                <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
            )}

            {loading ? (
                <Spinner size={SpinnerSize.large} label="Loading summary..." />
            ) : result && (
                <Stack horizontal wrap tokens={{ childrenGap: 12 }} styles={{ root: { rowGap: 12 } }}>
                    <StatCard title="Task lists" value={String(result.taskListCount)} />
                    <StatCard title="Tasks" value={String(result.taskCount)} />
                    <StatCard title="Work items" value={String(result.workItemCount)} />
                    <StatCard title="Jobs in progress" value={String(result.inProgressJobCount)} />
                    <StatCard title="Failed jobs" value={String(result.failedJobCount)} />
                    <StatCard title="Draft PRs" value={String(result.draftCount)} />
                    <StatCard title="Pending internal review" value={String(result.pendingInternalReviewCount)} />
                    <StatCard title="Published PRs" value={String(result.publishedCount)} />
                    <StatCard title="Blocked PRs" value={String(result.blockedCount)} />
                    <StatCard title="Signed off PRs" value={String(result.signedOffCount)} />
                    <StatCard title="Completed" value={String(result.completedCount)} />
                    <StatCard title="Abandoned" value={String(result.abandonedCount)} />
                    <StatCard title="Tokens consumed" value={formatNumber(result.tokensConsumed, 0)} />
                    <StatCard title="Avg hours to signoff" value={formatNumber(result.averageHoursToSignoff)} />
                </Stack>
            )}
        </PageShell>
    );
}
